/*
 * Template component parsing.
 *
 * Lifted out of `pages/templates/view[id]/components/` because the broadcast
 * composer needs it too: `broadcasts.md` §4 item 11 found that any template
 * with a `{{1}}` placeholder sent the raw placeholder to the customer, and the
 * note that "the template's variable positions are already parsed elsewhere in
 * this app" pointed straight at this file.
 */
export interface ParsedButton {
  type?: string;
  text: string;
  url?: string;
  phone_number?: string;
}

export interface ParsedComponent {
  type: string;
  format?: string;
  text?: string;
  buttons?: ParsedButton[];
  /**
   * Meta's approval sample. For a media header the URL sits at
   * `example.header_handle[0]`, which is where an imported template carries it
   * and where a submitted one must have it.
   */
  example?: { header_handle?: string[] };
}

export function parseComponents(raw: string | null | undefined): ParsedComponent[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((c): ParsedComponent => ({
      type: String((c as { type?: unknown })?.type ?? 'UNKNOWN').toUpperCase(),
      format: typeof (c as { format?: unknown })?.format === 'string' ? (c as { format: string }).format : undefined,
      text: typeof (c as { text?: unknown })?.text === 'string' ? (c as { text: string }).text : undefined,
      example: readExample(c),
      buttons: Array.isArray((c as { buttons?: unknown })?.buttons)
        ? ((c as { buttons: unknown[] }).buttons.map((b) => ({
          type: typeof (b as { type?: unknown })?.type === 'string' ? (b as { type: string }).type : undefined,
          text: typeof (b as { text?: unknown })?.text === 'string' ? (b as { text: string }).text : '',
          url: typeof (b as { url?: unknown })?.url === 'string' ? (b as { url: string }).url : undefined,
          phone_number: typeof (b as { phone_number?: unknown })?.phone_number === 'string'
            ? (b as { phone_number: string }).phone_number
            : undefined
        })))
        : undefined
    }));
  } catch {
    return [];
  }
}

/** `example.header_handle` if it is there, in the shape the header builder reads. */
function readExample(c: unknown): { header_handle?: string[] } | undefined {
  const ex = (c as { example?: unknown })?.example;
  if (!ex || typeof ex !== 'object') return undefined;
  const handle = (ex as { header_handle?: unknown }).header_handle;
  if (!Array.isArray(handle)) return undefined;
  const urls = handle.filter((h): h is string => typeof h === 'string');
  return urls.length > 0 ? { header_handle: urls } : undefined;
}

export function extractVariables(components: ParsedComponent[]): number[] {
  const found = new Set<number>();
  for (const c of components) {
    if (c.text) {
      for (const match of c.text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
        const n = Number(match[1]);
        if (Number.isFinite(n)) found.add(n);
      }
    }
    if (c.buttons) {
      for (const b of c.buttons) {
        if (b.text) {
          for (const match of b.text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
            const n = Number(match[1]);
            if (Number.isFinite(n)) found.add(n);
          }
        }
      }
    }
  }
  return Array.from(found).sort((a, b) => a - b);
}
